import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, addDoc, updateDoc, doc, query, where, getDocs, Timestamp, getDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { X, Clock, Calendar, User, ChevronLeft, ChevronRight, Plus, AlertTriangle, Copy, RotateCcw, AlertCircle } from 'lucide-react';
import { db as firebaseDb } from '../firebase';
import ClientEditAppointmentModal from './ClientEditAppointmentModal';
import RecurrenceEditModal from './RecurrenceEditModal';
import { useTheme } from '../contexts/ThemeContext';

const ClientWeekViewMobile = React.memo(({
  db,
  client,
  currentDate,
  startOfWeek: propStartOfWeek,
  appointments: propAppointments,
  employees: propEmployees,
  formatDate,
  formatTime,
  hasPermission,
  onAction
}) => {
  const safeDb = db || firebaseDb;
  const [weekDates, setWeekDates] = useState([]);
  const [internalAppointments, setInternalAppointments] = useState([]);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [internalEmployees, setInternalEmployees] = useState([]);
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(1); // Default to Monday (1)

  // Utiliser les props si fournies, sinon l'état interne
  const appointments = propAppointments || internalAppointments;
  const setAppointments = setInternalAppointments;
  const employees = propEmployees || internalEmployees;
  const setEmployees = setInternalEmployees;

  // Utiliser propStartOfWeek s'il existe, sinon currentDate
  const effectiveCurrentDate = propStartOfWeek || currentDate || new Date();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [unassignedAppointments, setUnassignedAppointments] = useState([]);
  const [appointmentsWithColors, setAppointmentsWithColors] = useState([]);
  const { theme } = useTheme();

  // Ajoutez un nouvel état pour stocker l'employé sélectionné
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [pendingAppointmentUpdate, setPendingAppointmentUpdate] = useState(null);

  const isToday = useCallback((date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  }, []);

  const fetchPlanningSettings = useCallback(async () => {
    if (!safeDb) return;
    try {
      const settingsDoc = await getDoc(doc(safeDb, 'settings', 'general'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setFirstDayOfWeek(settings.firstDayOfWeek || 1); // Default to Monday if not set
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres de planification:", error);
    }
  }, [db]);

  useEffect(() => {
    fetchPlanningSettings();
  }, [fetchPlanningSettings]);

  const updateWeekDates = useCallback((date) => {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diff = (day < firstDayOfWeek ? 7 : 0) + day - firstDayOfWeek;
    weekStart.setDate(weekStart.getDate() - diff);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      dates.push(day);
    }
    setWeekDates(dates);
  }, [firstDayOfWeek]);

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('isArchived', '==', false));
      const querySnapshot = await getDocs(q);
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data(),
        color: doc.data().color || '#808080' // Couleur par défaut si non définie
      }));

      // Filtrer pour exclure les comptes bénéficiaires
      const filteredEmployees = fetchedEmployees.filter(employee => {
        if (employee.role === 'beneficiaire') {
          return false;
        }

        const isActive = employee.isActive !== false && !employee.isArchived;
        return isActive;
      });

      setEmployees(filteredEmployees);
      console.log("Employés récupérés (bénéficiaires exclus):", filteredEmployees);
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [safeDb]);

  const sortAppointments = useCallback((apps) => {
    return apps.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return a.startTime.localeCompare(b.startTime);
    });
  }, []);

  // Modifier la fonction fetchAppointments pour inclure les rendez-vous non assignés
  const fetchAppointments = useCallback(async () => {
    if (!safeDb || !client.id) return;
    try {
      const appointmentsRef = collection(safeDb, 'appointments');
      const q = query(appointmentsRef, where('clientId', '==', client.id));
      const querySnapshot = await getDocs(q);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));

      const sortedAppointments = sortAppointments(fetchedAppointments);

      const assigned = sortedAppointments.filter(app => app.status === 'assigne');
      const toFill = sortedAppointments.filter(app => app.status !== 'assigne');

      setAppointments(assigned);
      setUnassignedAppointments(toFill);

      const withColors = assigned.map(app => ({
        ...app,
        employeeColor: employees.find(emp => emp.userId === app.employeeId)?.color || '#808080'
      }));
      setAppointmentsWithColors(withColors);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous:", error);
    }
  }, [db, client.id, employees, sortAppointments]);

  useEffect(() => {
    fetchPlanningSettings();
  }, [fetchPlanningSettings]);


  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);


  useEffect(() => {
    updateWeekDates(effectiveCurrentDate);
  }, [effectiveCurrentDate, updateWeekDates]);

  useEffect(() => {
    if (safeDb && client.id) {
      fetchAppointments();
    }
  }, [safeDb, client.id, fetchAppointments]);



  useEffect(() => {
    // Mettre à jour les couleurs des rendez-vous lorsque les employés changent
    const updatedAppointments = appointments.map(appointment => {
      const employee = employees.find(emp => emp.userId === appointment.employeeId);
      return {
        ...appointment,
        employeeColor: employee?.color || '#808080'
      };
    });
    setAppointments(updatedAppointments);
  }, [employees]);


  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    if (employee) {
      return employee.name || `${employee.firstName} ${employee.lastName}`;
    } else {
      console.warn(`Employé non trouvé pour l'ID: ${employeeId}`);
      return "Employé non trouvé";
    }
  };

  const isSameDay = (date1, date2) => {
    if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
      console.error('Invalid date object:', { date1, date2 });
      return false;
    }
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };





  // Modifiez la fonction handleCellClick
  const handleCellClick = (date, employeeId) => {
    setSelectedDate(new Date(date));
    setSelectedEmployee(employeeId);
    setShowAddModal(true);
  };

  const handleAppointmentDoubleClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleUpdateAppointment = async (updatedAppointment) => {
    // Si c'est un rendez-vous récurrent, afficher le modal de confirmation
    if (updatedAppointment.isRecurring) {
      setPendingAppointmentUpdate(updatedAppointment);
      setShowRecurrenceModal(true);
      return;
    }

    // Pour les rendez-vous non récurrents, mettre à jour normalement
    await updateSingleAppointment(updatedAppointment);
  };

  const handleRecurrenceConfirm = async (scope) => {
    if (!pendingAppointmentUpdate) return;

    try {
      if (scope === 'this') {
        // Mettre à jour seulement ce rendez-vous
        await updateSingleAppointment(pendingAppointmentUpdate);
      } else {
        // Pour les portées 'future' et 'all', nous devons trouver tous les rendez-vous de la série
        const allAppointments = await fetchAllRecurringAppointments(pendingAppointmentUpdate);

        let appointmentsToUpdate = [];

        if (scope === 'future') {
          // Mettre à jour tous les rendez-vous à partir de celui-ci
          const currentDate = new Date(pendingAppointmentUpdate.date);
          appointmentsToUpdate = allAppointments.filter(app =>
            new Date(app.date) >= currentDate
          );
        } else if (scope === 'all') {
          // Mettre à jour tous les rendez-vous de la série
          appointmentsToUpdate = allAppointments;
        }

        // Mettre à jour tous les rendez-vous sélectionnés
        const batch = writeBatch(db);
        appointmentsToUpdate.forEach(appointment => {
          const appointmentRef = doc(db, 'appointments', appointment.id);
          const updateData = {
            ...pendingAppointmentUpdate,
            id: appointment.id, // Garder l'ID original
            date: Timestamp.fromDate(new Date(appointment.date)), // Garder la date originale
            status: pendingAppointmentUpdate.employeeId ? 'assigne' : 'libre'
          };
          batch.update(appointmentRef, updateData);
        });

        await batch.commit();
        console.log(`Mise à jour de ${appointmentsToUpdate.length} rendez-vous récurrents`);

        // Rafraîchir les données
        fetchAppointments();
      }

      setIsEditModalOpen(false);
      setShowRecurrenceModal(false);
      setPendingAppointmentUpdate(null);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des rendez-vous récurrents:", error);
    }
  };

  const updateSingleAppointment = async (appointment) => {
    try {
      const appointmentRef = doc(db, 'appointments', appointment.id);
      const updateData = {
        ...appointment,
        date: Timestamp.fromDate(appointment.date),
        status: appointment.employeeId ? 'assigne' : 'libre',
        // Supprimer l'ID de série pour que ce rendez-vous ne fasse plus partie de la série récurrente
        recurrenceSeriesId: null
      };

      await updateDoc(appointmentRef, updateData);

      // Mise à jour de l'état local
      setAppointments(prevAppointments => {
        const updatedAppointments = prevAppointments.map(app =>
          app.id === appointment.id ? { ...appointment, recurrenceSeriesId: null } : app
        );
        return sortAppointments(updatedAppointments);
      });

      setAppointmentsWithColors(prevAppointments => {
        const updatedAppointments = prevAppointments.map(app => {
          if (app.id === appointment.id) {
            const employee = employees.find(emp => emp.userId === appointment.employeeId);
            return {
              ...appointment,
              recurrenceSeriesId: null,
              employeeColor: employee?.color || '#808080'
            };
          }
          return app;
        });
        return sortAppointments(updatedAppointments);
      });

      setIsEditModalOpen(false);
      console.log("Rendez-vous mis à jour avec succès (série rompue):", appointment);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous:", error);
    }
  };

  const fetchAllRecurringAppointments = async (appointment) => {
    try {
      // Si l'appointment a un recurrenceSeriesId, utiliser celui-ci
      if (appointment.recurrenceSeriesId) {
        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('clientId', '==', client.id),
          where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
        );
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date(),
        }));
      } else {
        // Fallback pour les anciens rendez-vous sans recurrenceSeriesId
        // Récupérer tous les rendez-vous du même client
        const appointmentsRef = collection(db, 'appointments');
        const q = query(appointmentsRef, where('clientId', '==', client.id));
        const querySnapshot = await getDocs(q);

        const allAppointments = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date(),
        }));

        // Filtrer pour trouver les rendez-vous de la même série récurrente
        // (basé sur les mêmes paramètres de récurrence)
        return allAppointments.filter(app =>
          app.isRecurring &&
          app.startTime === appointment.startTime &&
          app.endTime === appointment.endTime &&
          app.recurrenceBase === appointment.recurrenceBase
        );
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous récurrents:", error);
      return [];
    }
  };

  const handleDeleteAppointment = async (appointmentId, scope = 'this') => {
    try {
      if (scope === 'this') {
        // Supprimer seulement ce rendez-vous
        await deleteDoc(doc(db, 'appointments', appointmentId));
        setAppointments(prevAppointments =>
          prevAppointments.filter(app => app.id !== appointmentId)
        );
        setAppointmentsWithColors(prevAppointments =>
          prevAppointments.filter(app => app.id !== appointmentId)
        );
      } else if (scope === 'future') {
        // Supprimer tous les rendez-vous futurs de la série
        const appointment = appointments.find(app => app.id === appointmentId);
        if (appointment && appointment.recurrenceSeriesId) {
          const appointmentsRef = collection(db, 'appointments');
          const q = query(
            appointmentsRef,
            where('clientId', '==', client.id),
            where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
          );
          const querySnapshot = await getDocs(q);

          const batch = writeBatch(db);
          const currentDate = new Date(appointment.date);

          querySnapshot.docs.forEach(doc => {
            const appData = doc.data();
            const appDate = appData.date?.toDate() || new Date();
            if (appDate >= currentDate) {
              batch.delete(doc.ref);
            }
          });

          await batch.commit();

          // Mettre à jour l'état local
          setAppointments(prevAppointments =>
            prevAppointments.filter(app => {
              const appDate = new Date(app.date);
              return appDate < currentDate || app.recurrenceSeriesId !== appointment.recurrenceSeriesId;
            })
          );
          setAppointmentsWithColors(prevAppointments =>
            prevAppointments.filter(app => {
              const appDate = new Date(app.date);
              return appDate < currentDate || app.recurrenceSeriesId !== appointment.recurrenceSeriesId;
            })
          );
        }
      }
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
    }
  };


  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    if (hours > 0 && minutes > 0) return `${hours}h${minutes}`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}min`;
  };

  const getStatusBadge = (appointment) => {
    if (!appointment.employeeId) {
      return { text: 'Non assigné', bgColor: '#fee2e2', textColor: '#991b1b' };
    }
    if (appointment.status === 'assigne') {
      return { text: 'Confirmé', bgColor: '#d1fae5', textColor: '#065f46' };
    }
    if (appointment.status === 'reserve') {
      return { text: 'En attente', bgColor: '#fef3c7', textColor: '#92400e' };
    }
    return { text: 'Libre', bgColor: '#dbeafe', textColor: '#1e40af' };
  };

  const renderDayHeader = (date) => {
    const dayAppointments = appointments.filter(app =>
      app.date.toDateString() === date.toDateString()
    );
    const count = dayAppointments.length;
    const today = isToday(date);

    return (
      <div className="cwvm-day-header">
        <div className="cwvm-day-name">
          {formatDate(date, 'eee. d MMMM')}
          {today && <span className="cwvm-today-badge">Aujourd'hui</span>}
        </div>
        {count > 0 && (
          <div className="cwvm-appointments-count">
            {count} rendez-vous
          </div>
        )}
      </div>
    );
  };

  const renderAppointmentsForDay = (date) => {
    const dayAppointments = appointments.filter(app =>
      app.date.toDateString() === date.toDateString()
    ).sort((a, b) => a.startTime.localeCompare(b.startTime));

    return (
      <div className="cwvm-day-column">
        {renderDayHeader(date)}
        <div className="cwvm-appointments">
          {dayAppointments.length === 0 ? (
            <div className="cwvm-empty-state">
              <div className="cwvm-empty-state-icon">📅</div>
              <div className="cwvm-empty-state-text">Aucun rendez-vous</div>
            </div>
          ) : (
            dayAppointments.map((appointment, index) => {
              let borderColor = '#e53e3e';
              let statusClass = 'non-assigned';

              if (appointment.employeeId) {
                const employee = employees.find(emp => emp.userId === appointment.employeeId);
                if (appointment.status === 'assigne') {
                  borderColor = employee?.color || '#4a5568';
                  statusClass = 'assigned';
                } else {
                  borderColor = '#ed8936';
                  statusClass = 'pending';
                }
              }

              const duration = calculateDuration(appointment.startTime, appointment.endTime);
              const statusBadge = getStatusBadge(appointment);

              return (
                <div
                  key={index}
                  className={`cwvm-appointment ${statusClass} relative`}
                  style={{ borderLeftColor: borderColor }}
                  onDoubleClick={() => handleAppointmentDoubleClick(appointment)}
                >
                  {appointment.notes && appointment.notes.content && (
                    <div className="absolute top-1 right-1 notes-indicator-compact">
                      <span className="text-xs bg-purple-100 text-purple-800 p-1 rounded-full">
                        📝
                      </span>
                    </div>
                  )}

                  <div className="cwvm-appointment-time">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Clock size={14} className="cwvm-icon" />
                      {`${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`}
                    </div>
                    <span className="cwvm-appointment-duration">⏱ {duration}</span>
                  </div>
                  <div className="cwvm-appointment-employee">
                    <User size={14} className="cwvm-icon" />
                    {appointment.employeeId ? getEmployeeName(appointment.employeeId) : "Non assigné"}
                  </div>
                  <div
                    className="cwvm-appointment-status"
                    style={{
                      backgroundColor: statusBadge.bgColor,
                      color: statusBadge.textColor
                    }}
                  >
                    {statusBadge.text}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="cwvm-container">
      <div className="cwvm-week-view">
        {weekDates.map((date, index) => (
          <React.Fragment key={index}>
            {renderAppointmentsForDay(date)}
          </React.Fragment>
        ))}
      </div>

      {isEditModalOpen && selectedAppointment && (
        <Modal onClose={handleCloseEditModal}>
          <ClientEditAppointmentModal
            appointment={selectedAppointment}
            employees={employees}
            onSave={handleUpdateAppointment}
            onDelete={handleDeleteAppointment}
            onClose={handleCloseEditModal}
            canEditAppointments={true}
          />
        </Modal>
      )}

      {showRecurrenceModal && pendingAppointmentUpdate && (
        <RecurrenceEditModal
          isOpen={showRecurrenceModal}
          onClose={() => setShowRecurrenceModal(false)}
          onConfirm={handleRecurrenceConfirm}
          appointment={pendingAppointmentUpdate}
        />
      )}
    </div>
  );
});


const Modal = ({ children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="pv-modal w-full max-w-md animate-fade-in-down">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700">
          <X size={24} />
        </button>
        {children}
      </div>
    </div>
  );
};

// Ajouter le displayName pour le débogage
ClientWeekViewMobile.displayName = 'ClientWeekViewMobile';

export default ClientWeekViewMobile;
